
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface UserCredits {
  user_id: string;
  credits: number;
  created_at: string;
  updated_at: string;
}

type UserCreditsChanges = RealtimePostgresChangesPayload<UserCredits>;

const POLLING_INTERVAL = 10000; // Poll every 10 seconds as a fallback

function isUserCredits(obj: any): obj is UserCredits {
  return obj 
    && typeof obj === 'object' 
    && 'credits' in obj 
    && typeof obj.credits === 'number';
}

export const useCredits = (user: User | null) => {
  const [credits, setCredits] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      setCredits(null);
      return;
    }

    try {
      console.log('[useCredits] Fetching credits for user:', user.id);
      const { data, error: fetchError } = await supabase
        .from('user_credits')
        .select('credits')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        console.error('[useCredits] Fetch error:', fetchError);
        throw fetchError;
      }

      if (data) {
        console.log('[useCredits] Credits loaded:', data.credits);
        setCredits(data.credits);
        setError(null);
      } else {
        console.log('[useCredits] No credits found for user:', user.id);
        setCredits(0);
      }
    } catch (err) {
      console.error('[useCredits] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch credits'));
      // Only show toast if we're not in the initial loading state
      if (!isLoading) {
        toast({
          title: "Error",
          description: "Failed to fetch credits. Retrying...",
          variant: "destructive",
        });
      }
      // Don't set credits to null on error to preserve last known value
    } finally {
      setIsLoading(false);
    }
  }, [user, toast, isLoading]);

  // Realtime subscription setup
  useEffect(() => {
    if (!user) {
      setCredits(null);
      setIsLoading(false);
      return;
    }

    console.log('[useCredits] Setting up subscription for user:', user.id);
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_credits',
          filter: `user_id=eq.${user.id}`,
        },
        (payload: UserCreditsChanges) => {
          console.log('[useCredits] Received change:', payload);
          if (isUserCredits(payload.new)) {
            setCredits(payload.new.credits);
            setError(null); // Clear any previous errors on successful update
          }
        }
      )
      .subscribe(status => {
        console.log('[useCredits] Subscription status:', status);
      });

    // Initial fetch after subscription is set up
    fetchCredits();

    return () => {
      console.log('[useCredits] Cleaning up subscription');
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchCredits]);

  // Polling as a fallback
  useEffect(() => {
    if (!user) return;
    
    const pollInterval = setInterval(fetchCredits, POLLING_INTERVAL);

    return () => {
      clearInterval(pollInterval);
    };
  }, [user, fetchCredits]);

  const deductCredits = useCallback(async (): Promise<boolean> => {
    if (!user || credits === null) return false;
    if (credits <= 0) {
      toast({
        title: "Error",
        description: "No credits available",
        variant: "destructive",
      });
      return false;
    }

    try {
      console.log('[useCredits] Deducting credit for user:', user.id);
      const { error: deductError } = await supabase
        .from('user_credits')
        .update({ credits: credits - 1 })
        .eq('user_id', user.id);

      if (deductError) throw deductError;

      return true;
    } catch (err) {
      console.error('[useCredits] Deduction error:', err);
      toast({
        title: "Error",
        description: "Failed to deduct credits",
        variant: "destructive",
      });
      return false;
    }
  }, [user, credits, toast]);

  return {
    credits,
    isLoading,
    error,
    refetch: fetchCredits,
    deductCredits
  };
};

